import { useCallback, useEffect, useRef, useState } from 'react'
import { feedStreamUrl, getFeedEvents } from '../../api/admin'
import { FEED_REPLAY_CAP, type FeedEventView } from '../../adminApi'

/**
 * The event feed's client.
 *
 * No React rendering concerns here — it returns `{ events, status, … }` — and the `EventSource`
 * constructor is injectable so a fake can emit frames, drop and reconnect on demand. That is why
 * it is a hook and not logic inside the panel.
 *
 * `feed_events.id` is monotonic and every path is *"give me everything after `id`"*. The cursor
 * **never goes backwards**, so a replay of an event we already hold is dropped by id and a
 * reconnect can be as sloppy as it likes.
 *
 * If `EventSource` fails twice the client falls back to polling and *says so in the header*
 * ("live" vs "polling"). The backoffice must work behind a proxy that mangles streaming.
 */

export type FeedStatus = 'connecting' | 'live' | 'polling' | 'disconnected'

/** Which events the operator wants to see. Persisted, because a matchday outlasts a page load. */
export type FeedMute = 'none' | 'system-only' | 'match-only' | 'all'

export const MUTE_STORAGE_KEY = 'rwbet.backoffice.feedMute'
export const POLL_INTERVAL_MS = 10_000
/** Two failed connections is enough: a third attempt against a buffering proxy fails identically. */
export const MAX_STREAM_FAILURES = 2
/** How many events the panel keeps in memory. The record is the server's; this is the view. */
export const MAX_HELD_EVENTS = 500

export interface EventFeedController {
  events: FeedEventView[]
  status: FeedStatus
  /** Highest id held. Rendered in the panel header — it is how you tell a stalled feed. */
  cursor: number
  /** True when a replay hit the server's cap and older events were dropped. */
  truncated: boolean
  mute: FeedMute
  setMute: (mute: FeedMute) => void
  error: string | null
}

export interface EventFeedOptions {
  enabled: boolean
  /** Injected so a test can drive frames, drops and reconnects without a network. */
  eventSourceFactory?: (url: string) => EventSourceLike
  pollIntervalMs?: number
  /** Injected for the same reason; defaults to the real endpoint. */
  fetchPage?: typeof getFeedEvents
}

/** The slice of `EventSource` this hook uses — so a fake needs four members, not the whole API. */
export interface EventSourceLike {
  addEventListener(type: string, listener: (event: MessageEvent) => void): void
  close(): void
  onerror: ((event: unknown) => void) | null
}

function readStoredMute(): FeedMute {
  try {
    const stored = localStorage.getItem(MUTE_STORAGE_KEY)
    if (stored === 'none' || stored === 'system-only' || stored === 'match-only' || stored === 'all') {
      return stored
    }
  } catch {
    // A browser with storage disabled still gets a working feed, just not a remembered filter.
  }
  return 'none'
}

/** Applied at render, never at ingest: muting is about what you *see*, and un-muting must not
 *  have lost the last ten minutes. */
export function applyMute(events: FeedEventView[], mute: FeedMute): FeedEventView[] {
  if (mute === 'none') return events
  if (mute === 'all') return []
  if (mute === 'system-only') return events.filter((event) => event.scope === 'system')
  return events.filter((event) => event.scope === 'match')
}

export function useEventFeed(options: EventFeedOptions): EventFeedController {
  const { enabled, eventSourceFactory, pollIntervalMs = POLL_INTERVAL_MS, fetchPage = getFeedEvents } =
    options

  const [events, setEvents] = useState<FeedEventView[]>([])
  const [status, setStatus] = useState<FeedStatus>('connecting')
  const [truncated, setTruncated] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mute, setMuteState] = useState<FeedMute>(readStoredMute)

  // The cursor lives in a ref as well as in state: the stream callback needs to read the current
  // value without re-subscribing, and a stale closure here means replaying the same events forever.
  const cursor = useRef(0)
  const [cursorState, setCursorState] = useState(0)

  const ingest = useCallback((incoming: FeedEventView[]) => {
    if (incoming.length === 0) return
    setEvents((previous) => {
      const held = new Set(previous.map((event) => event.id))
      const fresh = incoming.filter((event) => !held.has(event.id))
      if (fresh.length === 0) return previous
      const merged = [...previous, ...fresh].sort((a, b) => a.id - b.id)
      return merged.slice(-MAX_HELD_EVENTS)
    })
    const highest = Math.max(...incoming.map((event) => event.id))
    // Never backwards. A reconnect that replays from an older cursor must not rewind us.
    if (highest > cursor.current) {
      cursor.current = highest
      setCursorState(highest)
    }
  }, [])

  const setMute = useCallback((next: FeedMute) => {
    setMuteState(next)
    try {
      localStorage.setItem(MUTE_STORAGE_KEY, next)
    } catch {
      // See `readStoredMute` — a disabled store costs the preference, not the feed.
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    let failures = 0
    let source: EventSourceLike | null = null
    let pollTimer: ReturnType<typeof setInterval> | null = null

    const poll = async () => {
      try {
        const page = await fetchPage({ since: cursor.current, limit: FEED_REPLAY_CAP })
        if (cancelled) return
        ingest(page.events)
        setTruncated(page.truncated)
        setError(null)
      } catch (failure) {
        if (cancelled) return
        setStatus('disconnected')
        setError(failure instanceof Error ? failure.message : String(failure))
      }
    }

    const startPolling = () => {
      if (cancelled || pollTimer) return
      setStatus('polling')
      void poll()
      pollTimer = setInterval(() => void poll(), pollIntervalMs)
    }

    const connect = () => {
      if (cancelled) return
      const factory =
        eventSourceFactory ??
        ((url: string) => new EventSource(url, { withCredentials: true }) as EventSourceLike)

      setStatus('connecting')
      try {
        source = factory(feedStreamUrl(cursor.current))
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : String(failure))
        startPolling()
        return
      }

      source.addEventListener('hello', (event) => {
        if (cancelled) return
        const payload = JSON.parse(event.data) as { truncated?: boolean }
        setTruncated(Boolean(payload.truncated))
        setStatus('live')
        setError(null)
        failures = 0
      })

      source.addEventListener('feed', (event) => {
        if (cancelled) return
        ingest([JSON.parse(event.data) as FeedEventView])
        setStatus('live')
      })

      source.onerror = () => {
        if (cancelled) return
        source?.close()
        source = null
        failures += 1
        if (failures >= MAX_STREAM_FAILURES) {
          // Twice is enough. A proxy that buffers `text/event-stream` will do it again.
          startPolling()
          return
        }
        setStatus('connecting')
        connect()
      }
    }

    connect()

    return () => {
      cancelled = true
      source?.close()
      if (pollTimer) clearInterval(pollTimer)
    }
  }, [enabled, eventSourceFactory, fetchPage, ingest, pollIntervalMs])

  return { events, status, cursor: cursorState, truncated, mute, setMute, error }
}
